import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const DayInTheLife = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activePhase, setActivePhase] = useState(0);

  const workflowPhases = [
    {
      time: "9:00 AM",
      title: "Morning Standup",
      description: "Team sync to discuss daily goals, blockers, and priorities",
      icon: "Users",
      color: "from-blue-500 to-cyan-500",
      activities: [
        "Review yesterday\'s progress",
        "Discuss today\'s priorities",
        "Identify potential blockers",
        "Align on sprint goals"
      ],
      participants: ["Anbu Liyon", "Giridharan"]
    },
    {
      time: "9:30 AM",
      title: "Deep Work Session",
      description: "Focused development time with minimal interruptions",
      icon: "Code2",
      color: "from-green-500 to-emerald-500",
      activities: [
        "Feature development",
        "Bug fixes and optimization",
        "Code refactoring",
        "Documentation updates"
      ],
      participants: ["Individual Focus Time"]
    },
    {
      time: "12:00 PM",
      title: "Code Review",
      description: "Collaborative review of pull requests and code quality",
      icon: "GitPullRequest",
      color: "from-purple-500 to-pink-500",
      activities: [
        "Review pull requests",
        "Provide constructive feedback",
        "Discuss implementation approaches",
        "Ensure code standards compliance"
      ],
      participants: ["Peer Review Teams"]
    },
    {
      time: "2:00 PM",
      title: "Client Communication",
      description: "Regular updates and feedback sessions with clients",
      icon: "MessageCircle",
      color: "from-orange-500 to-red-500",
      activities: [
        "Progress demonstrations",
        "Gather client feedback",
        "Discuss feature requirements",
        "Plan next iterations"
      ],
      participants: ["Project Leads", "Clients"]
    },
    {
      time: "3:30 PM",
      title: "Collaborative Development",
      description: "Pair programming and knowledge sharing sessions",
      icon: "Users2",
      color: "from-cyan-500 to-blue-500",
      activities: [
        "Pair programming sessions",
        "Knowledge transfer",
        "Problem-solving discussions",
        "Mentoring junior developers"
      ],
      participants: ["Development Pairs"]
    },
    {
      time: "5:00 PM",
      title: "Learning & Innovation",
      description: "Dedicated time for learning new technologies and experimentation",
      icon: "BookOpen",
      color: "from-indigo-500 to-purple-500",
      activities: [
        "Explore new technologies",
        "Work on side projects",
        "Contribute to open source",
        "Prepare tech talks"
      ],
      participants: ["Individual Learning"]
    }
  ];

  const teamValues = [
    {
      title: "Code with Purpose",
      description: "Every line of code serves a clear business objective and user need",
      icon: "Target",
      example: "Before implementing a feature, we always ask: 'How does this solve the user's problem?'"
    },
    {
      title: "Learn Continuously",
      description: "Embrace new technologies and methodologies to stay ahead of the curve",
      icon: "TrendingUp",
      example: "Weekly tech talks where team members share discoveries and best practices"
    },
    {
      title: "Deliver Excellence",
      description: "Maintain high standards in code quality, testing, and user experience",
      icon: "Award",
      example: "Comprehensive code reviews and automated testing ensure every release meets our quality bar"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePhase((prev) => (prev + 1) % workflowPhases.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-foreground mb-4">A Day in the Life at bytepairs</h3>
        <p className="text-text-secondary max-w-2xl mx-auto mb-6">
          Experience our collaborative development process that ensures quality, efficiency, and continuous growth.
        </p>
        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-lg">
          <Icon name="Clock" size={16} className="text-primary" />
          <span className="text-primary font-medium">
            Current Time: {currentTime.toLocaleTimeString()}
          </span>
        </div>
      </div>

      {/* Workflow Timeline */}
      <div className="relative">
        <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-primary to-accent rounded-full" />

        <div className="space-y-8">
          {workflowPhases.map((phase, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
            >
              <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                <motion.div
                  className={`card p-6 ${activePhase === index ? 'ring-2 ring-primary shadow-brand-lg' : ''} transition-all duration-300`}
                  animate={{
                    scale: activePhase === index ? 1.02 : 1,
                    y: activePhase === index ? -5 : 0
                  }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${phase.color} flex items-center justify-center`}>
                      <Icon name={phase.icon} size={20} color="white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-foreground">{phase.title}</h4>
                      <p className="text-primary font-medium text-sm">{phase.time}</p>
                    </div>
                  </div>

                  <p className="text-text-secondary text-sm mb-4">{phase.description}</p>

                  <div className="space-y-2">
                    {phase.activities.map((activity, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Icon name="CheckCircle2" size={14} className="text-success" />
                        <span className="text-text-secondary text-sm">{activity}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center space-x-2">
                      <Icon name="Users" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary text-xs">
                        {phase.participants.join(", ")}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="relative z-10">
                <motion.div
                  className={`w-6 h-6 rounded-full border-4 border-background ${activePhase === index ? 'bg-primary' : 'bg-muted'
                    } transition-colors duration-300`}
                  animate={{
                    scale: activePhase === index ? 1.2 : 1
                  }}
                />
              </div>

              <div className="w-1/2" />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Team Values */}
      <div>
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-foreground mb-4">Our Core Values in Action</h3>
          <p className="text-text-secondary max-w-2xl mx-auto">
            These principles guide every decision we make and every line of code we write.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {teamValues.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="card p-6 text-center group hover:shadow-brand-lg transition-all duration-300"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Icon name={value.icon} size={24} color="white" />
              </div>

              <h4 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                {value.title}
              </h4>
              <p className="text-text-secondary text-sm mb-4">{value.description}</p>

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-foreground text-sm font-medium italic">
                  "{value.example}"
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Boutique Advantage */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="card p-8 bg-gradient-to-r from-primary/5 to-accent/5"
      >
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-foreground mb-4">The bytepairs Boutique Advantage</h3>
          <p className="text-lg text-text-secondary mb-6">
            At bytepairs you get direct access to our dedicated development team throughout your entire project lifecycle.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Users" size={20} className="text-primary" />
              <span>Direct Developer Access</span>
            </h4>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircle2" size={16} className="text-success mt-0.5" />
                <span className="text-text-secondary text-sm">Direct communication with skilled developers</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircle2" size={16} className="text-success mt-0.5" />
                <span className="text-text-secondary text-sm">Consistent team throughout the project</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircle2" size={16} className="text-success mt-0.5" />
                <span className="text-text-secondary text-sm">Consistent quality and architectural decisions</span>
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Zap" size={20} className="text-accent" />
              <span>Efficient Communication</span>
            </h4>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircle2" size={16} className="text-success mt-0.5" />
                <span className="text-text-secondary text-sm">Faster decision making with fewer approval layers</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircle2" size={16} className="text-success mt-0.5" />
                <span className="text-text-secondary text-sm">Technical discussions directly with implementers</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="CheckCircle2" size={16} className="text-success mt-0.5" />
                <span className="text-text-secondary text-sm">Rapid iteration and feedback implementation</span>
              </li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DayInTheLife;